/**
 * SimultaneousDamageAnimation - Groups combat events into simultaneous damage/death animation units.
 *
 * Responsibilities:
 * - Group consecutive attack → damage → death events into a single animation unit
 * - Clearly represent that attacker and defender take damage at the same time
 * - Handle double-death (both minions dying from the same exchange)
 * - Handle single-death and no-death cases
 * - Provide render data for the animation layer to display simultaneous effects
 * - Track animation phase within each group (attack → damage → death)
 *
 * The backend is authoritative; this module only groups and presents events
 * for visual animation. It does not simulate combat logic.
 */

import {
  AnimatedCombatEvent,
  AnimatedCombatEventType,
} from './CombatAnimationView';
import {
  CombatDamageEvent,
  CombatAttackEvent,
  CombatDeathEvent,
} from '../../shared/messages/MessageContracts';

// ─── Animation Phase ────────────────────────────────────────────────────────

/**
 * The current phase within a simultaneous damage animation group.
 * Each group progresses through these phases in order.
 */
export type SimultaneousDamagePhase =
  | 'attack'    // Attacker targets defender (movement animation)
  | 'damage'    // Both take damage simultaneously (damage numbers flash)
  | 'death'     // Dead minions are removed (death animation)
  | 'complete'; // Animation group fully resolved

// ─── Simultaneous Damage Animation Group ─────────────────────────────────────

/**
 * A grouped animation unit for a single simultaneous damage exchange.
 *
 * During combat, an attack results in:
 * 1. Attack event — attacker targets defender
 * 2. Damage event — both take damage at the SAME time
 * 3. Zero or more death events — minions dying from the exchange
 *
 * This group ties those events together so the animation layer can
 * display them as a single simultaneous action rather than sequential steps.
 */
export interface SimultaneousDamageAnimationGroup {
  /** Unique identifier for this animation group (derived from the damage event). */
  groupId: string;

  /** The attack event, if available (may be null if grouped from damage alone). */
  attackEvent: AnimatedCombatEvent | null;

  /** The damage event — always present, this is the anchor of the group. */
  damageEvent: AnimatedCombatEvent;

  /** Death events that resulted from this damage exchange. */
  deathEvents: AnimatedCombatEvent[];

  /** Instance ID of the attacking minion. */
  attackerInstanceId: string;

  /** Instance ID of the defending minion. */
  defenderInstanceId: string;

  /** Damage dealt to the attacker (by the defender's counter-attack). */
  damageToAttacker: number;

  /** Damage dealt to the defender (by the attacker). */
  damageToDefender: number;

  /** Attacker's HP after the damage exchange. */
  attackerHpAfter: number;

  /** Defender's HP after the damage exchange. */
  defenderHpAfter: number;

  /** Whether the attacker died from this exchange. */
  attackerDied: boolean;

  /** Whether the defender died from this exchange. */
  defenderDied: boolean;

  /** Whether both minions died (double-death scenario). */
  isDoubleDeath: boolean;

  /** Whether exactly one minion died. */
  isSingleDeath: boolean;

  /** Whether neither minion died. */
  isNoDeath: boolean;

  /** Current animation phase within this group. */
  currentPhase: SimultaneousDamagePhase;

  /** Whether this group has been fully consumed/animated. */
  consumed: boolean;
}

// ─── Simultaneous Damage Render Data ────────────────────────────────────────

/**
 * Render data for displaying simultaneous damage/death animations.
 */
export interface SimultaneousDamageRenderData {
  /** All grouped animation units. */
  groups: SimultaneousDamageAnimationGroup[];

  /** The next group waiting to be animated (if any). */
  nextGroup: SimultaneousDamageAnimationGroup | null;

  /** Number of groups waiting to be animated. */
  pendingCount: number;

  /** Number of groups already consumed. */
  consumedCount: number;

  /** Total number of groups. */
  totalCount: number;

  /** Whether all groups have been consumed. */
  allConsumed: boolean;

  /** Whether there is a double-death group pending. */
  hasPendingDoubleDeath: boolean;

  /** Whether there is any death pending. */
  hasPendingDeath: boolean;
}

/**
 * Callback invoked when render data changes.
 */
export type SimultaneousDamageRenderCallback = (data: SimultaneousDamageRenderData) => void;

// ─── Simultaneous Damage Animator ────────────────────────────────────────────

/**
 * Groups combat events from the animation view into simultaneous damage/death
 * animation units and provides render data for the animation layer.
 *
 * Usage:
 * 1. Feed events from CombatAnimationView into this animator
 * 2. Call buildGroups() to group events
 * 3. Consume groups in order via consumeNextGroup()
 * 4. Use renderData for UI binding
 */
export class SimultaneousDamageAnimator {
  private _renderCallbacks: SimultaneousDamageRenderCallback[] = [];
  private _groups: SimultaneousDamageAnimationGroup[] = [];
  private _pendingEvents: AnimatedCombatEvent[] = [];

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data. */
  get renderData(): SimultaneousDamageRenderData {
    return this._buildRenderData();
  }

  /** Returns all groups (consumed and pending). */
  get groups(): SimultaneousDamageAnimationGroup[] {
    return [...this._groups];
  }

  // ─── Event Feeding ───────────────────────────────────────────────────────

  /**
   * Feed a new event from the CombatAnimationView into the animator.
   * Events are buffered and grouped when buildGroups() is called.
   */
  feedEvent(event: AnimatedCombatEvent): void {
    this._pendingEvents.push(event);
  }

  /**
   * Feed multiple events at once.
   */
  feedEvents(events: AnimatedCombatEvent[]): void {
    for (const event of events) {
      this._pendingEvents.push(event);
    }
  }

  /**
   * Clear all pending events and groups (reset).
   */
  reset(): void {
    this._pendingEvents = [];
    this._groups = [];
    this._notifyRenderCallbacks();
  }

  // ─── Grouping ────────────────────────────────────────────────────────────

  /**
   * Build animation groups from pending events.
   *
   * Groups are formed by finding damage events and attaching:
   * - The preceding attack event (if any)
   * - Any following death events (before the next damage event)
   *
   * Events that don't belong to a damage group (combatStarted, killGold,
   * ability, combatResult) are left as ungrouped events.
   */
  buildGroups(): void {
    const events = this._pendingEvents;
    this._pendingEvents = [];

    if (events.length === 0) {
      this._notifyRenderCallbacks();
      return;
    }

    // Find all damage events as anchors
    const damageEventIndices: number[] = [];
    for (let i = 0; i < events.length; i++) {
      if (events[i].eventType === 'damage') {
        damageEventIndices.push(i);
      }
    }

    for (const damageIndex of damageEventIndices) {
      const damageEvent = events[damageIndex];
      const damageData = damageEvent.message as CombatDamageEvent;

      // Find preceding attack event (look backward from damage event)
      let attackEvent: AnimatedCombatEvent | null = null;
      for (let i = damageIndex - 1; i >= 0; i--) {
        const candidate = events[i];
        if (candidate.eventType === 'attack') {
          // Check if this attack matches the damage event's minions
          const attackData = candidate.message as CombatAttackEvent;
          if (attackData.attackerInstanceId === damageData.attackerInstanceId &&
              attackData.defenderInstanceId === damageData.defenderInstanceId) {
            attackEvent = candidate;
            break;
          }
        }
        // Stop searching if we hit another damage event or combatStarted
        if (candidate.eventType === 'damage' || candidate.eventType === 'combatStarted') {
          break;
        }
      }

      // Find following death events (look forward until next damage event or end)
      const deathEvents: AnimatedCombatEvent[] = [];
      for (let i = damageIndex + 1; i < events.length; i++) {
        const candidate = events[i];
        if (candidate.eventType === 'death') {
          deathEvents.push(candidate);
        } else if (candidate.eventType === 'damage') {
          // Stop at the next damage event — belongs to a different group
          break;
        } else if (candidate.eventType === 'combatStarted' || candidate.eventType === 'combatResult') {
          break;
        } else {
          // killGold, ability events — skip them (don't break, continue looking for deaths)
          continue;
        }
      }

      // Determine death flags from death events
      const attackerDied = deathEvents.some((de) => {
        const deathData = de.message as CombatDeathEvent;
        return deathData.minionInstanceId === damageData.attackerInstanceId;
      });

      const defenderDied = deathEvents.some((de) => {
        const deathData = de.message as CombatDeathEvent;
        return deathData.minionInstanceId === damageData.defenderInstanceId;
      });

      // Also check HP-based death as fallback (0 or negative HP)
      const hpAttackerDied = damageData.attackerHpAfter <= 0;
      const hpDefenderDied = damageData.defenderHpAfter <= 0;

      const finalAttackerDied = attackerDied || hpAttackerDied;
      const finalDefenderDied = defenderDied || hpDefenderDied;

      // Generate group ID from damage event participants
      const groupId = `damage-${damageData.attackerInstanceId}-${damageData.defenderInstanceId}-${damageEvent.sequenceIndex}`;

      const group: SimultaneousDamageAnimationGroup = {
        groupId,
        attackEvent,
        damageEvent,
        deathEvents,
        attackerInstanceId: damageData.attackerInstanceId,
        defenderInstanceId: damageData.defenderInstanceId,
        damageToAttacker: damageData.damageToAttacker,
        damageToDefender: damageData.damageToDefender,
        attackerHpAfter: damageData.attackerHpAfter,
        defenderHpAfter: damageData.defenderHpAfter,
        attackerDied: finalAttackerDied,
        defenderDied: finalDefenderDied,
        isDoubleDeath: finalAttackerDied && finalDefenderDied,
        isSingleDeath: finalAttackerDied !== finalDefenderDied,
        isNoDeath: !finalAttackerDied && !finalDefenderDied,
        currentPhase: this._calculateInitialPhase(attackEvent, deathEvents, finalAttackerDied, finalDefenderDied),
        consumed: false,
      };

      this._groups.push(group);
    }

    this._notifyRenderCallbacks();
  }

  /**
   * Calculate the initial animation phase for a group based on its contents.
   */
  private _calculateInitialPhase(
    attackEvent: AnimatedCombatEvent | null,
    deathEvents: AnimatedCombatEvent[],
    attackerDied: boolean,
    defenderDied: boolean,
  ): SimultaneousDamagePhase {
    if (attackEvent) {
      return 'attack';
    }
    if (deathEvents.length > 0 || attackerDied || defenderDied) {
      return 'damage';
    }
    return 'damage';
  }

  // ─── Group Consumption ───────────────────────────────────────────────────

  /**
   * Consume the next pending group in order.
   * Returns the group if available, null if no pending groups.
   *
   * Groups must be consumed in order (like events).
   */
  consumeNextGroup(): SimultaneousDamageAnimationGroup | null {
    for (let i = 0; i < this._groups.length; i++) {
      if (!this._groups[i].consumed) {
        this._groups[i].consumed = true;
        this._notifyRenderCallbacks();
        return this._groups[i];
      }
    }
    return null;
  }

  /**
   * Peek at the next pending group without consuming it.
   */
  peekNextGroup(): SimultaneousDamageAnimationGroup | null {
    for (let i = 0; i < this._groups.length; i++) {
      if (!this._groups[i].consumed) {
        return this._groups[i];
      }
    }
    return null;
  }

  /**
   * Advance the current phase of a group.
   * Returns the new phase, or null if the group is already complete or not found.
   */
  advanceGroupPhase(groupId: string): SimultaneousDamagePhase | null {
    const group = this._groups.find((g) => g.groupId === groupId);
    if (!group || group.consumed) {
      return null;
    }

    switch (group.currentPhase) {
      case 'attack':
        group.currentPhase = 'damage';
        return 'damage';
      case 'damage':
        group.currentPhase = group.isNoDeath ? 'complete' : 'death';
        return group.currentPhase;
      case 'death':
        group.currentPhase = 'complete';
        return 'complete';
      case 'complete':
        return null; // Already complete
    }
  }

  /**
   * Consume all pending groups at once.
   */
  consumeAllGroups(): SimultaneousDamageAnimationGroup[] {
    const consumed: SimultaneousDamageAnimationGroup[] = [];
    let group;
    while ((group = this.consumeNextGroup()) !== null) {
      consumed.push(group);
    }
    return consumed;
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   */
  onRenderChange(callback: SimultaneousDamageRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Internal ────────────────────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _buildRenderData(): SimultaneousDamageRenderData {
    const pendingGroups = this._groups.filter((g) => !g.consumed);
    const consumedGroups = this._groups.filter((g) => g.consumed);
    const nextGroup = pendingGroups.length > 0 ? pendingGroups[0] : null;

    return {
      groups: [...this._groups],
      nextGroup,
      pendingCount: pendingGroups.length,
      consumedCount: consumedGroups.length,
      totalCount: this._groups.length,
      allConsumed: pendingGroups.length === 0,
      hasPendingDoubleDeath: pendingGroups.some((g) => g.isDoubleDeath),
      hasPendingDeath: pendingGroups.some((g) => g.isSingleDeath || g.isDoubleDeath),
    };
  }
}
